import React, { useState, useRef, useEffect } from "react";
import GeminiLogo from "../Images/Gemini.png";

interface GeminiInfoSidebarProps {
  isDark?: boolean;
}

const GeminiInfoSidebar: React.FC<GeminiInfoSidebarProps> = ({ isDark }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isClicked, setIsClicked] = useState(false);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);
  const [isMobileView, setIsMobileView] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth <= 768);
    };
    window.addEventListener("resize", handleResize);
    // Call it once to set initial state
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setIsClicked(false);
      }
    };

    if (isClicked) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isClicked]);

  // Original Desktop Styles (base)
  const baseSidebarStyle: React.CSSProperties = {
    position: "fixed",
    top: "10%",
    right: isOpen ? "0" : "-400px",
    width: "400px",
    height: "80vh",
    background: isDark
      ? "rgba(25, 25, 30, 0.85)"
      : "rgba(255, 255, 255, 0.95)",
    borderRadius: "16px",
    boxShadow: "0 4px 30px rgba(0, 0, 0, 0.4)",
    backdropFilter: "blur(8px)",
    WebkitBackdropFilter: "blur(8px)",
    border: "1px solid rgba(255, 255, 255, 0.1)",
    color: isDark ? "white" : "#000",
    padding: "25px",
    boxSizing: "border-box",
    transition: "right 0.3s ease-in-out, width 0.3s ease-in-out, height 0.3s ease-in-out, top 0.3s ease-in-out, padding 0.3s ease-in-out",
    overflowY: "auto",
    zIndex: 10000,
  };

  const baseTitleStyle: React.CSSProperties = {
    background: "linear-gradient(120deg, #00DBDE 0%, #FC00FF 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    fontSize: "32px",
    fontWeight: 700,
    letterSpacing: "-0.5px",
    marginBottom: "10px",
  };

  const baseIconContainerStyle: React.CSSProperties = {
    width: "80px",
    height: "80px",
    background: isDark
      ? "rgba(255, 255, 255, 0.08)"
      : "rgba(0, 0, 0, 0.04)",
    borderRadius: "50%",
    margin: "0 auto 15px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 0 20px rgba(0, 219, 222, 0.15)",
  };

  const baseLogoStyle: React.CSSProperties = {
    width: "52px",
    height: "52px",
    objectFit: "contain",
  };

  const baseDescriptionStyle: React.CSSProperties = {
    fontSize: "14px",
    opacity: "0.8",
    marginBottom: "15px",
  };

  const baseTextContainerStyle: React.CSSProperties = {
    textAlign: "center",
    marginBottom: "20px"
  };

  const baseTagStyle: React.CSSProperties = {
    display: "inline-block",
    background: "rgba(220, 38, 38, 0.15)",
    color: isDark ? "#fca5a5" : "#dc2626",
    border: `1px solid ${isDark ? "rgba(252, 165, 165, 0.3)" : "rgba(220, 38, 38, 0.3)"}`,
    borderRadius: "6px",
    padding: "4px 12px",
    fontSize: "12px",
    fontWeight: 600,
    letterSpacing: "0.5px",
    marginBottom: "16px",
  };

  const baseAbilitiesContainerStyle: React.CSSProperties = {
    display: "flex",
    justifyContent: "center",
    gap: "8px",
    flexWrap: "wrap",
    marginBottom: "20px",
  };

  const baseAbilityChipStyle: React.CSSProperties = {
    background: isDark
      ? "rgba(255, 255, 255, 0.08)"
      : "rgba(0, 0, 0, 0.05)",
    border: `1px solid ${isDark ? "rgba(255, 255, 255, 0.12)" : "rgba(0, 0, 0, 0.1)"}`,
    borderRadius: "20px",
    padding: "5px 14px",
    fontSize: "12px",
    fontWeight: 500,
    color: isDark ? "rgba(255, 255, 255, 0.85)" : "rgba(0, 0, 0, 0.7)",
  };

  const baseSectionStyle: React.CSSProperties = { marginBottom: "25px" };

  const baseSectionHeadingStyle: React.CSSProperties = {
    borderBottom: isDark
      ? "1px solid rgba(255,255,255,0.15)"
      : "1px solid rgba(0,0,0,0.1)",
    paddingBottom: "8px",
    marginBottom: "12px",
    fontSize: "15px",
    fontWeight: 600,
    letterSpacing: "0.2px",
    color: isDark ? "rgba(255,255,255,0.9)" : "rgba(0,0,0,0.85)",
  };

  const baseListStyle: React.CSSProperties = {
    paddingLeft: "20px",
    fontSize: "14px",
    lineHeight: "1.6",
    margin: 0,
  };

  const baseParagraphStyle: React.CSSProperties = {
    fontSize: "14px",
    lineHeight: "1.6",
    margin: 0,
    opacity: 0.9,
  };

  const baseTechSpecGridStyle: React.CSSProperties = {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "10px",
    fontSize: "14px",
  };

  const baseTechSpecItemStyle: React.CSSProperties = {
    background: isDark
      ? "rgba(255,255,255,0.06)"
      : "rgba(0,0,0,0.03)",
    padding: "10px",
    borderRadius: "8px",
    textAlign: "center",
    border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"}`,
  };

  // New: refined tool-row styles for the MCP Tools section
  const baseToolListStyle: React.CSSProperties = {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  };

  const baseToolRowStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "flex-start",
    gap: "10px",
    background: isDark
      ? "rgba(255,255,255,0.04)"
      : "rgba(0,0,0,0.025)",
    border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"}`,
    borderRadius: "10px",
    padding: "10px 12px",
  };

  const baseToolBulletStyle: React.CSSProperties = {
    flexShrink: 0,
    width: "6px",
    height: "6px",
    marginTop: "8px",
    borderRadius: "50%",
    background: "linear-gradient(120deg, #00DBDE 0%, #FC00FF 100%)",
    boxShadow: "0 0 6px rgba(0, 219, 222, 0.5)",
  };

  const baseToolNameStyle: React.CSSProperties = {
    fontSize: "13px",
    fontWeight: 600,
    color: isDark ? "rgba(255,255,255,0.95)" : "rgba(0,0,0,0.9)",
    marginBottom: "2px",
    lineHeight: 1.3,
  };

  const baseToolDescStyle: React.CSSProperties = {
    fontSize: "12.5px",
    lineHeight: 1.45,
    color: isDark ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.65)",
  };

  const baseFooterTextStyle: React.CSSProperties = {
    fontSize: "12px",
    opacity: "0.6",
    textAlign: "center",
    marginTop: "20px",
  };

  // Mobile-specific overrides
  const mobileSidebarStyle: React.CSSProperties = isMobileView ? {
    width: "90vw",
    height: "85vh",
    top: "7.5%",
    padding: "15px",
    right: isOpen ? "5vw" : "-95vw",
  } : {};

  const mobileTitleStyle: React.CSSProperties = isMobileView ? { fontSize: "24px", marginBottom: "8px" } : {};
  const mobileIconContainerStyle: React.CSSProperties = isMobileView ? { width: "60px", height: "60px", margin: "0 auto 10px" } : {};
  const mobileLogoStyle: React.CSSProperties = isMobileView ? { width: "38px", height: "38px" } : {};
  const mobileDescriptionStyle: React.CSSProperties = isMobileView ? { fontSize: "12px", marginBottom: "10px" } : {};
  const mobileTextContainerStyle: React.CSSProperties = isMobileView ? { marginBottom: "15px" } : {};
  const mobileTagStyle: React.CSSProperties = isMobileView ? { fontSize: "11px", padding: "3px 10px", marginBottom: "12px" } : {};
  const mobileAbilitiesContainerStyle: React.CSSProperties = isMobileView ? { gap: "6px", marginBottom: "15px" } : {};
  const mobileAbilityChipStyle: React.CSSProperties = isMobileView ? { fontSize: "11px", padding: "4px 10px" } : {};
  const mobileSectionStyle: React.CSSProperties = isMobileView ? { marginBottom: "15px" } : {};
  const mobileSectionHeadingStyle: React.CSSProperties = isMobileView ? { fontSize: "14px", paddingBottom: "6px", marginBottom: "10px" } : {};
  const mobileListStyle: React.CSSProperties = isMobileView ? { paddingLeft: "15px", fontSize: "12px", lineHeight: "1.5" } : {};
  const mobileParagraphStyle: React.CSSProperties = isMobileView ? { fontSize: "12px", lineHeight: "1.5" } : {};
  const mobileTechSpecGridStyle: React.CSSProperties = isMobileView ? { gridTemplateColumns: "1fr", gap: "8px", fontSize: "12px" } : {};
  const mobileTechSpecItemStyle: React.CSSProperties = isMobileView ? { padding: "8px" } : {};
  const mobileToolRowStyle: React.CSSProperties = isMobileView ? { padding: "8px 10px", gap: "8px" } : {};
  const mobileToolNameStyle: React.CSSProperties = isMobileView ? { fontSize: "12px" } : {};
  const mobileToolDescStyle: React.CSSProperties = isMobileView ? { fontSize: "11.5px", lineHeight: 1.4 } : {};
  const mobileFooterTextStyle: React.CSSProperties = isMobileView ? { fontSize: "10px", marginTop: "15px" } : {};

  // Merged styles
  const finalSidebarStyle = { ...baseSidebarStyle, ...mobileSidebarStyle };
  if (isMobileView && !isOpen) {
    finalSidebarStyle.right = "-95vw";
  } else if (!isMobileView && !isOpen) {
    finalSidebarStyle.right = "-400px";
  } else if (isMobileView && isOpen) {
    finalSidebarStyle.right = "5vw";
  } else {
    finalSidebarStyle.right = "0";
  }

  const finalTitleStyle = { ...baseTitleStyle, ...mobileTitleStyle };
  const finalIconContainerStyle = { ...baseIconContainerStyle, ...mobileIconContainerStyle };
  const finalLogoStyle = { ...baseLogoStyle, ...mobileLogoStyle };
  const finalDescriptionStyle = { ...baseDescriptionStyle, ...mobileDescriptionStyle };
  const finalTextContainerStyle = { ...baseTextContainerStyle, ...mobileTextContainerStyle };
  const finalTagStyle = { ...baseTagStyle, ...mobileTagStyle };
  const finalAbilitiesContainerStyle = { ...baseAbilitiesContainerStyle, ...mobileAbilitiesContainerStyle };
  const finalAbilityChipStyle = { ...baseAbilityChipStyle, ...mobileAbilityChipStyle };
  const finalSectionStyle = { ...baseSectionStyle, ...mobileSectionStyle };
  const finalSectionHeadingStyle = { ...baseSectionHeadingStyle, ...mobileSectionHeadingStyle };
  const finalListStyle = { ...baseListStyle, ...mobileListStyle };
  const finalParagraphStyle = { ...baseParagraphStyle, ...mobileParagraphStyle };
  const finalTechSpecGridStyle = { ...baseTechSpecGridStyle, ...mobileTechSpecGridStyle };
  const finalTechSpecItemStyle = { ...baseTechSpecItemStyle, ...mobileTechSpecItemStyle };
  const finalToolRowStyle = { ...baseToolRowStyle, ...mobileToolRowStyle };
  const finalToolNameStyle = { ...baseToolNameStyle, ...mobileToolNameStyle };
  const finalToolDescStyle = { ...baseToolDescStyle, ...mobileToolDescStyle };
  const finalFooterTextStyle = { ...baseFooterTextStyle, ...mobileFooterTextStyle };

  // MCP tools surfaced by the backend (user-facing copy only)
  const mcpTools: { name: string; desc: string }[] = [
    { name: "Google Search grounding", desc: "Pull live information from Google Search with inline source citations." },
    { name: "Code execution", desc: "Run code natively to crunch numbers, transform data, and verify answers." },
    { name: "Query your files and emails", desc: "Look across your synced files, documents, and inboxes to answer questions in context." },
    { name: "Check email", desc: "Inspect Gmail or Outlook messages, unread mail, recent senders, subjects, counts, and requested message contents without changing mailbox state." },
    { name: "Generate a file", desc: "Produce downloadable documents, spreadsheets, PDFs, code files, and other structured files on request." },
    { name: "Generate slide deck", desc: "Create professional presentations, pitch decks, training decks, board decks, and PowerPoint- or PDF-ready slide decks from your goals, source material, and style guidance." },
    { name: "Send or draft an email", desc: "Compose, draft, or send messages through your connected Gmail or Outlook account." },
    { name: "Find an email recipient", desc: "Look up someone in your recent contacts when you only know their name." },
    { name: "Edit a cloud file", desc: "Modify or rewrite files stored in your Google Drive or OneDrive." },
    { name: "Work with Desktop files", desc: "Edit, organize, rename, or move files on your synced Desktop, and create new folders." },
    { name: "Schedule or cancel a task", desc: "Run actions later, on a recurring schedule, or in the background — and stop them anytime." },
    { name: "Connect to GitHub repository", desc: "Browse and search authorized repositories, inspect pull requests, analyze CI failures, create branches, commit changes, and open pull requests." },
    { name: "Connect to any service", desc: "Link a new third-party app or platform on demand, even one not built in by default." },
    { name: "Log in to your account", desc: "Securely sign in to a connected service through a protected modal — never in chat." },
  ];

  return (
    <>
      {/* Info Button */}
      <div
        ref={buttonRef}
        onMouseEnter={() => !isClicked && setIsOpen(true)}
        onMouseLeave={() => !isClicked && setIsOpen(false)}
        onClick={() => {
          setIsClicked(!isClicked);
          setIsOpen(!isClicked);
        }}
        style={{
          color: isDark ? "#ffffff" : "#000000",
          fontSize: "20px",
          padding: "5px",
          cursor: "pointer",
          transition: "opacity 0.3s",
          display: "inline-flex",
          alignItems: "center",
          zIndex: 10001,
        }}
      >
        <img
          src={GeminiLogo}
          alt="Gemini Logo"
          style={{ width: "36px", height: "36px", objectFit: "contain" }}
        />
      </div>

      {/* Enhanced Sidebar */}
      <div
        ref={sidebarRef}
        style={finalSidebarStyle}
        onMouseEnter={() => !isClicked && setIsOpen(true)}
        onMouseLeave={() => !isClicked && setIsOpen(false)}
      >
        <div style={finalTextContainerStyle}>
          <div style={finalTitleStyle}>
            Gemini 3 Flash
          </div>
          <div style={finalIconContainerStyle}>
            <img
              src={GeminiLogo}
              alt="Gemini Logo"
              style={finalLogoStyle}
            />
          </div>
          <div style={finalDescriptionStyle}>
            Google DeepMind's latest fast multimodal model
          </div>
          <div style={finalTagStyle}>
            OpenClaw-enabled
          </div>
          <div style={finalAbilitiesContainerStyle}>
            <span style={finalAbilityChipStyle}>Check email</span>
            <span style={finalAbilityChipStyle}>Send an email</span>
            <span style={finalAbilityChipStyle}>Generate slides</span>
            <span style={finalAbilityChipStyle}>Edit a file</span>
            <span style={finalAbilityChipStyle}>Schedule a task</span>
            <span style={finalAbilityChipStyle}>Connect GitHub</span>
            <span style={finalAbilityChipStyle}>Connect a service</span>
          </div>
        </div>

        <div style={finalSectionStyle}>
          <h3 style={finalSectionHeadingStyle}>
            Overview
          </h3>
          <ul style={finalListStyle}>
            <li>Fast, cost-efficient reasoning across long contexts</li>
            <li>Native multimodal understanding of every common file type</li>
            <li>Google Search grounding with inline citations</li>
            <li>Built-in code execution for computation and data work</li>
            <li>Read-only email checking for recent, unread, or sender-specific messages</li>
            <li>Document, spreadsheet, PDF, and code-file generation</li>
            <li>Professional slide deck and PowerPoint generation</li>
            <li>GitHub repository assistance for code, pull requests, branches, and CI</li>
            <li>Streaming responses for real-time interaction</li>
          </ul>
        </div>

        <div style={finalSectionStyle}>
          <h3 style={finalSectionHeadingStyle}>
            Abilities
          </h3>
          <div style={baseToolListStyle}>
            {mcpTools.map((tool) => (
              <div key={tool.name} style={finalToolRowStyle}>
                <span style={baseToolBulletStyle} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={finalToolNameStyle}>{tool.name}</div>
                  <div style={finalToolDescStyle}>{tool.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div style={finalSectionStyle}>
          <h3 style={finalSectionHeadingStyle}>
            Model Details
          </h3>
          <div style={finalTechSpecGridStyle}>
            <div style={finalTechSpecItemStyle}>
              <div style={{ fontWeight: 600, marginBottom: "2px" }}>Model</div>
              <div style={{ opacity: 0.8 }}>gemini-3-flash-preview</div>
            </div>
            <div style={finalTechSpecItemStyle}>
              <div style={{ fontWeight: 600, marginBottom: "2px" }}>Provider</div>
              <div style={{ opacity: 0.8 }}>Google DeepMind</div>
            </div>
            <div style={finalTechSpecItemStyle}>
              <div style={{ fontWeight: 600, marginBottom: "2px" }}>Released</div>
              <div style={{ opacity: 0.8 }}>2026</div>
            </div>
            <div style={finalTechSpecItemStyle}>
              <div style={{ fontWeight: 600, marginBottom: "2px" }}>Streaming</div>
              <div style={{ opacity: 0.8 }}>Enabled</div>
            </div>
          </div>
        </div>

        <div style={finalSectionStyle}>
          <h3 style={finalSectionHeadingStyle}>
            Usage Notes
          </h3>
          <p style={finalParagraphStyle}>
            Gemini 3 Flash is built to move quickly across the things you
            actually need done — just describe the task in plain English. Ask
            it to look across your files and emails, check your inbox for
            recent or unread messages, ground an answer in live Google Search
            results, run code to crunch some numbers, draft and send messages
            through your connected mail account, generate a polished document,
            spreadsheet, or slide deck, tidy up files on your Desktop, work
            with an authorized GitHub repository, or set work to run later on a
            schedule. If a task involves a service it isn't already wired into,
            simply name the service and Gemini will offer to connect to it and
            securely sign you in through a protected modal.
          </p>
        </div>

        <div style={finalFooterTextStyle}>
          Powered by Google DeepMind
        </div>
      </div>
    </>
  );
};

export default GeminiInfoSidebar;